// Estatísticas de estudo: dias estudados, sequência (streak), totais e ajuste do ranking.
// Extraído de App.js sem alteração de comportamento.
import { useState, useEffect } from "react";
import { loadLS } from "./storage";
import { sb } from "./supabase";
import { CAL_SEED } from "./constants";
import { calEmpty } from "./calendar";
import { localDayKey } from "./dates";

// ── SEQUÊNCIA DE ESTUDO ──────────────────────────────────────────────────────
// Conta os dias seguidos com pelo menos STREAK_MIN_SEC de estudo no cronômetro
// (tabela study_sessions). Hoje ainda "em aberto" não quebra a sequência.
export const STREAK_MIN_SEC = 10 * 60;

export function useStudyDays(uid, mergeCalendar = false) {
  const [state, setState] = useState({ byDay: {}, sessions: {}, cal: null, loaded: false });
  useEffect(() => {
    if (!uid) return;
    let alive = true;
    (async () => {
      const from = new Date(); from.setFullYear(from.getFullYear() - 2); from.setMonth(0, 1); from.setHours(0, 0, 0, 0);
      const byDay = {}, sessions = {};
      try {
        for (let page = 0; page < 20; page++) {
          const { data, error } = await sb.from("study_sessions").select("seconds, recorded_at")
            .eq("user_id", uid).gte("recorded_at", from.toISOString()).order("recorded_at", { ascending: true })
            .range(page * 1000, page * 1000 + 999);
          if (error || !data) break;
          data.forEach(row => {
            if (!row.recorded_at) return;
            const k = localDayKey(new Date(row.recorded_at)), sec = Number(row.seconds) || 0;
            byDay[k] = (byDay[k] || 0) + sec;
            (sessions[k] = sessions[k] || []).push({ at: row.recorded_at, sec });
          });
          if (data.length < 1000) break;
        }
      } catch {}
      // Calendário: alimenta o gráfico de matérias; e, só na conta do administrador, os dias dele também contam na sequência.
      let cal = null;
      try {
        try { const { data: row } = await sb.from("study_calendar").select("data").eq("user_id", uid).maybeSingle(); cal = row && row.data; } catch {}
        if (!cal || !cal.days) cal = loadLS("g17_study_calendar_" + uid, null) || (mergeCalendar ? CAL_SEED : calEmpty());
        if (mergeCalendar) {
          Object.entries(cal.days || {}).forEach(([k, subj]) => {
            const sec = Object.values(subj || {}).reduce((a, m) => a + (Number(m) || 0), 0) * 60;
            if (sec > (byDay[k] || 0)) byDay[k] = sec;   // não soma duas vezes o que já veio do cronômetro
          });
        }
      } catch {}
      if (alive) setState({ byDay, sessions, cal, loaded: true });
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dependências mantidas como estavam (mudar alteraria quando o efeito roda)
  }, [uid]);
  return state;
}

export function computeStreak(byDay, today = new Date()) {
  const ok = k => (byDay[k] || 0) >= STREAK_MIN_SEC;
  const d = new Date(today); d.setHours(12, 0, 0, 0);
  const todayDone = ok(localDayKey(d));
  if (!todayDone) d.setDate(d.getDate() - 1);
  let current = 0;
  while (ok(localDayKey(d))) { current++; d.setDate(d.getDate() - 1); }
  // recorde
  const keys = Object.keys(byDay).filter(ok).sort();
  let best = 0, run = 0, prev = null;
  keys.forEach(k => {
    const x = new Date(k + "T12:00:00");
    if (prev) { const p = new Date(prev); p.setDate(p.getDate() + 1); run = localDayKey(p) === k ? run + 1 : 1; } else run = 1;
    prev = x; best = Math.max(best, run);
  });
  return { current, best: Math.max(best, current), todayDone, todaySec: byDay[localDayKey(new Date())] || 0 };
}

// Conquistas: usa o maior entre o cronômetro e o Calendário (administrador) e a sequência real de dias.
export function badgeExtra(sd, isAdmin, baseSec) {
  if (!sd || !sd.loaded) return { totalSec: baseSec, extra: undefined };
  let totalSec = baseSec || 0;
  if (isAdmin) {
    totalSec = calendarTotals(sd.cal).total;
  }
  return { totalSec, extra: { streakBest: computeStreak(sd.byDay || {}).best, force: isAdmin ? ["simulado_1"] : [] } };
}

// Ranking: só para a conta do administrador, o tempo vem direto do total acumulado do Calendário.
function calendarTotals(cal) {
  const keyOf = d => localDayKey(d);
  const sumDay = k => Object.values((cal && cal.days && cal.days[k]) || {}).reduce((a, v) => a + (Number(v) || 0), 0) * 60;
  const now = new Date(); now.setHours(12, 0, 0, 0);
  const monday = new Date(now); monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));   // semana começa na segunda (igual ao ranking)
  let week = 0;
  for (let d = new Date(monday); d <= now; d.setDate(d.getDate() + 1)) week += sumDay(keyOf(d));
  const days = Object.keys((cal && cal.days) || {}).reduce((a, k) => a + sumDay(k), 0);
  const bulk = Object.values((cal && cal.bulk) || {}).reduce((a, m) => a + Object.values(m || {}).reduce((x, v) => x + (Number(v) || 0), 0), 0) * 60;
  return { day: sumDay(keyOf(now)), week, total: days + bulk };
}

export function useMyRankOverride(uid, isAdmin) {
  const sd = useStudyDays(isAdmin ? uid : null, true);
  if (!isAdmin || !sd.loaded || !sd.cal) return null;
  return calendarTotals(sd.cal);
}
